import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { Plus } from "lucide-react";
import { useWorkerList } from "../../hooks/useWorkerList";
import { Button } from "../../components/Button";
import { WorkerList } from "./WorkerList";
import { NewWorkerModal } from "./NewWorkerModal";

/**
 * Workers admin page — PM-only (WORKER-7.6).
 * Default: active workers only. "Show revoked" toggle includes revoked rows.
 */
export function WorkersPage() {
  const [showRevoked, setShowRevoked] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const { data: workers = [], isLoading } = useWorkerList(showRevoked);

  return (
    <div className="ps-8 pe-8 pbs-8 pbe-8" data-testid="workers-page">
      <header className="flex items-center justify-between mbe-6">
        <h1 className="text-2xl font-semibold text-on-surface">
          <FormattedMessage id="LABEL_WORKERS" />
        </h1>
        <Button
          variant="primary"
          onClick={() => setCreateModalOpen(true)}
          data-testid="new-worker-button"
        >
          <span className="inline-flex items-center gap-2">
            <Plus size={16} aria-hidden="true" />
            <FormattedMessage id="LABEL_NEW_WORKER" />
          </span>
        </Button>
      </header>

      <div className="flex items-center gap-4 mbe-4">
        <button
          type="button"
          role="switch"
          aria-checked={showRevoked}
          onClick={() => setShowRevoked((v) => !v)}
          data-testid="show-revoked-toggle"
          className={`ps-3 pe-3 pbs-1 pbe-1 rounded-full text-sm font-semibold border transition-colors duration-200 ${
            showRevoked
              ? "bg-secondary-container text-on-secondary-container border-primary"
              : "bg-surface-variant text-on-surface-variant border-divider"
          }`}
        >
          <FormattedMessage id="LABEL_SHOW_REVOKED" />
        </button>
        <span className="text-sm text-text-secondary" data-testid="worker-count">
          <FormattedMessage
            id="LABEL_WORKERS_COUNT"
            values={{ count: workers.length }}
          />
        </span>
      </div>

      {isLoading ? (
        <div className="text-center pbs-12 pbe-12 text-text-secondary" data-testid="workers-loading">
          {"…"}
        </div>
      ) : workers.length === 0 ? (
        <div className="text-center pbs-12 pbe-12" data-testid="workers-empty-state">
          <h2 className="text-2xl font-semibold text-on-surface mbe-2">
            <FormattedMessage id="LABEL_NO_WORKERS_HEADING" />
          </h2>
          <p className="text-base text-text-secondary">
            <FormattedMessage id="LABEL_NO_WORKERS_BODY" />
          </p>
        </div>
      ) : (
        <WorkerList workers={workers} />
      )}

      {createModalOpen && (
        <NewWorkerModal onClose={() => setCreateModalOpen(false)} />
      )}
    </div>
  );
}
